var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { Box, ClickAwayListener, Tooltip, Typography } from '@mui/material';
var PERCENTAGE_BAR_CHART_STRINGS = appStrings.modules.charts.percentageBarChart;
export var PercentageBarChart = function (_a) {
    var value = _a.value, title = _a.title, tooltipText = _a.tooltipText, topMarker = _a.topMarker, bottomMarker = _a.bottomMarker;
    var _b = useState(false), isTooltipOpen = _b[0], setIsTooltipOpen = _b[1];
    var displayValue = Math.round(value * 100);
    var topMarkerDisplay = topMarker ? Math.round(topMarker.value * 100) : undefined;
    var bottomMarkerDisplay = bottomMarker ? Math.round(bottomMarker.value * 100) : undefined;
    return (_jsxs(Box, { sx: containerSx, children: [_jsxs(Box, { sx: headerSx, children: [_jsx(Typography, { variant: "text-sm-semibold", children: title }), _jsx(ClickAwayListener, { onClickAway: function () { return setIsTooltipOpen(false); }, children: _jsx(Tooltip, { open: isTooltipOpen, onClose: function () { return setIsTooltipOpen(false); }, title: tooltipText, placement: "top", arrow: true, disableFocusListener: true, disableHoverListener: true, disableTouchListener: true, slotProps: {
                                tooltip: {
                                    sx: function (_a) {
                                        var backrs = _a.backrs;
                                        return ({
                                            whiteSpace: 'pre-line',
                                            padding: backrs.spacing['spacing-xl'],
                                        });
                                    },
                                },
                            }, children: _jsx(Box, { id: "percentage-bar-info-".concat(title.toLowerCase().replace(/\s+/g, '-')), onClick: function () { return setIsTooltipOpen(function (prev) { return !prev; }); }, sx: iconContainerSx, children: _jsx(AppIcon, { iconName: "InfoCircle", diameter: "20px", color: "Colors/Foreground/fg-quinary" }) }) }) })] }), _jsxs(Box, { sx: contentSx, children: [_jsxs(Typography, { variant: "display-sm-semibold", sx: valueSx, children: [displayValue, "%"] }), _jsxs(Box, { sx: markersAboveBarSx, children: [_jsx(Box, { sx: __assign(__assign({}, tickMarkerPositionSx), { left: '0%' }), children: _jsx(Typography, { variant: "text-xs-regular", sx: markerLabelSx, children: PERCENTAGE_BAR_CHART_STRINGS.minLabel }) }), topMarker && topMarkerDisplay !== undefined && (_jsxs(Box, { sx: __assign(__assign({}, markerPositionSx), { left: "".concat(topMarkerDisplay, "%") }), children: [_jsxs(Typography, { variant: "text-xs-regular", sx: markerLabelSx, children: [topMarker.label, ": ", topMarkerDisplay, "%"] }), _jsx(Box, { sx: triangleDownSx })] })), _jsx(Box, { sx: __assign(__assign({}, tickMarkerPositionSx), { left: '100%' }), children: _jsx(Typography, { variant: "text-xs-regular", sx: markerLabelSx, children: PERCENTAGE_BAR_CHART_STRINGS.maxLabel }) })] }), _jsxs(Box, { sx: barContainerSx, children: [_jsx(Box, { sx: getBarFillSx("".concat(displayValue, "%")) }), topMarker && topMarkerDisplay !== undefined && (_jsx(Box, { sx: getMarkerLineSx(topMarkerDisplay) })), bottomMarker && bottomMarkerDisplay !== undefined && (_jsx(Box, { sx: getMarkerLineSx(bottomMarkerDisplay) }))] }), bottomMarker && bottomMarkerDisplay !== undefined && (_jsx(Box, { sx: markerContainerBelowSx, children: _jsxs(Box, { sx: __assign(__assign({}, markerPositionSx), { left: "".concat(bottomMarkerDisplay, "%") }), children: [_jsx(Box, { sx: triangleUpSx }), _jsxs(Typography, { variant: "text-xs-regular", sx: markerLabelSx, children: [bottomMarker.label, ": ", bottomMarkerDisplay, "%"] })] }) }))] })] }));
};
var containerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        borderRadius: backrs.radius['radius-xl'],
        backgroundColor: backrs.colors['Colors/Base/White'],
    });
};
var headerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingX: backrs.spacing['spacing-2xl'],
        paddingY: backrs.spacing['spacing-lg'],
    });
};
var iconContainerSx = {
    cursor: 'pointer',
};
var contentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        padding: backrs.spacing['spacing-2xl'],
        paddingTop: backrs.spacing['spacing-lg'],
        border: "1px solid ".concat(backrs.colors['Colors/Border/border-primary']),
        borderRadius: backrs.radius['radius-xl'],
    });
};
var valueSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        mb: backrs.spacing['spacing-lg'],
    });
};
var markersAboveBarSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        height: backrs.spacing['spacing-4xl'],
        marginBottom: backrs.spacing['spacing-xs'],
    });
};
var barContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        height: backrs.spacing['spacing-3xl'],
        backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
        borderRadius: backrs.radius['radius-sm'],
        border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        overflow: 'hidden',
        position: 'relative',
    });
};
var getBarFillSx = function (width) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            height: '100%',
            width: width,
            backgroundColor: COLOR_TOKENS['Brand/500'],
            borderTopRightRadius: backrs.radius['radius-sm'],
            borderBottomRightRadius: backrs.radius['radius-sm'],
            position: 'relative',
        });
    };
};
var markerContainerBelowSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        height: backrs.spacing['spacing-4xl'],
        marginTop: backrs.spacing['spacing-xs'],
    });
};
var markerPositionSx = {
    position: 'absolute',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
var tickMarkerPositionSx = {
    position: 'absolute',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    bottom: 0,
};
var markerLabelSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-tertiary'],
        whiteSpace: 'nowrap',
    });
};
var triangleDownSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: "6px solid ".concat(backrs.colors['Colors/Foreground/fg-quaternary']),
        marginTop: backrs.spacing['spacing-xxs'],
    });
};
var triangleUpSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: "6px solid ".concat(backrs.colors['Colors/Foreground/fg-quaternary']),
        marginBottom: backrs.spacing['spacing-xxs'],
    });
};
var getMarkerLineSx = function (positionPercent) { return ({
    position: 'absolute',
    top: 0,
    left: "".concat(positionPercent, "%"),
    height: '100%',
    width: '12px',
    backgroundColor: COLOR_TOKENS['Colors/Base/Black'],
    transform: 'translateX(calc(-50% + 1px))',
    zIndex: 10,
    pointerEvents: 'none',
}); };
